import type { Timezone } from '@ipfabric/utils/timezones';
import type {
  CronEveryOption,
  CronFormData,
  CronOptionStates,
  CustomOptionState,
  DayOptionState,
  HourOptionState,
  MonthOptionState,
  WeekOptionState,
  YearOptionState,
} from './cronTypes';

const emptyMonthValue: string[] = [];
const emptyDayValue: string[] = [];
const emptyWeekValue: string[] = [];
const emptyHourValue = '';
const emptyMinuteValue = '';
const emptyCustomValue = '';

/**
 * Create empty state for each option type
 */
export function createEmptyYearState(): YearOptionState {
  return {
    months: [...emptyMonthValue],
    days: [...emptyDayValue],
    hour: emptyHourValue,
    minute: emptyMinuteValue,
  };
}

export function createEmptyMonthState(): MonthOptionState {
  return {
    days: [...emptyDayValue],
    hour: emptyHourValue,
    minute: emptyMinuteValue,
  };
}

export function createEmptyWeekState(): WeekOptionState {
  return {
    weekDays: [...emptyWeekValue],
    hour: emptyHourValue,
    minute: emptyMinuteValue,
  };
}

export function createEmptyDayState(): DayOptionState {
  return {
    hour: emptyHourValue,
    minute: emptyMinuteValue,
  };
}

export function createEmptyHourState(): HourOptionState {
  return {
    minute: emptyMinuteValue,
  };
}

export function createEmptyCustomState(): CustomOptionState {
  return {
    minute: emptyCustomValue,
    hour: emptyCustomValue,
    days: emptyCustomValue,
    months: emptyCustomValue,
    weekDays: emptyCustomValue,
  };
}

/**
 * Create initial empty states for all options
 */
export function createEmptyOptionStates(): CronOptionStates {
  return {
    year: createEmptyYearState(),
    month: createEmptyMonthState(),
    week: createEmptyWeekState(),
    day: createEmptyDayState(),
    hour: createEmptyHourState(),
    custom: createEmptyCustomState(),
  };
}

/**
 * Converts a comma-separated string or array to an array of strings
 * Handles empty strings by returning an empty array
 */
function normalizeToArray(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (value === undefined || value === '') {
    return [];
  }

  return value.split(',').filter((item) => item.trim() !== '');
}

/**
 * Converts a comma-separated string or array to a string
 * Arrays are joined with commas, empty arrays return empty string
 */
function normalizeToString(value: string | string[] | undefined): string {
  if (Array.isArray(value)) {
    return value.join(',');
  }

  return value ?? '';
}

/**
 * Extract option-specific state from CronFormData
 * Handles conversion between string and array formats based on option type
 */
export function extractOptionState(
  formData: CronFormData,
  option: CronEveryOption,
): CronOptionStates[CronEveryOption] {
  switch (option) {
    case 'year': {
      const months = normalizeToArray(formData.months);
      const days = normalizeToArray(formData.days);
      return {
        months,
        days,
        hour: formData.hour || emptyHourValue,
        minute: formData.minute || emptyMinuteValue,
      };
    }
    case 'month': {
      const days = normalizeToArray(formData.days);
      return {
        days,
        hour: formData.hour || emptyHourValue,
        minute: formData.minute || emptyMinuteValue,
      };
    }
    case 'week': {
      const weekDays = normalizeToArray(formData.weekDays);
      return {
        weekDays,
        hour: formData.hour || emptyHourValue,
        minute: formData.minute || emptyMinuteValue,
      };
    }
    case 'day':
      return {
        hour: formData.hour || emptyHourValue,
        minute: formData.minute || emptyMinuteValue,
      };
    case 'hour':
      return {
        minute: formData.minute || emptyMinuteValue,
      };
    case 'custom': {
      const days = normalizeToString(formData.days);
      const months = normalizeToString(formData.months);
      const weekDays = normalizeToString(formData.weekDays);
      return {
        minute: formData.minute || emptyCustomValue,
        hour: formData.hour || emptyCustomValue,
        days: days || emptyCustomValue,
        months: months || emptyCustomValue,
        weekDays: weekDays || emptyCustomValue,
      };
    }
  }
}

/**
 * Convert option state back to CronFormData format
 */
export function optionStateToFormData(
  optionState: CronOptionStates[CronEveryOption],
  option: CronEveryOption,
  timezone: Timezone,
): CronFormData {
  switch (option) {
    case 'year': {
      const state = optionState as YearOptionState;
      return {
        every: 'year',
        months: state.months,
        days: state.days,
        weekDays: [],
        hour: state.hour,
        minute: state.minute,
        timezone,
      };
    }
    case 'month': {
      const state = optionState as MonthOptionState;
      return {
        every: 'month',
        months: [],
        days: state.days,
        weekDays: [],
        hour: state.hour,
        minute: state.minute,
        timezone,
      };
    }
    case 'week': {
      const state = optionState as WeekOptionState;
      return {
        every: 'week',
        months: [],
        days: [],
        weekDays: state.weekDays,
        hour: state.hour,
        minute: state.minute,
        timezone,
      };
    }
    case 'day': {
      const state = optionState as DayOptionState;
      return {
        every: 'day',
        months: [],
        days: [],
        weekDays: [],
        hour: state.hour,
        minute: state.minute,
        timezone,
      };
    }
    case 'hour': {
      const state = optionState as HourOptionState;
      return {
        every: 'hour',
        months: [],
        days: [],
        weekDays: [],
        // Hour slot must stay numeric to satisfy the shared zod schema,
        // yet the cron builder always replaces it with a wildcard. Using "0"
        // keeps validation happy without affecting the generated expression.
        hour: '0',
        minute: state.minute,
        timezone,
      };
    }
    case 'custom': {
      const state = optionState as CustomOptionState;
      return {
        every: 'custom',
        months: state.months,
        days: state.days,
        weekDays: state.weekDays,
        hour: state.hour,
        minute: state.minute,
        timezone,
      };
    }
  }
}
